// IM-XPR-005 (IM-UX-118, IM-UI-101) — Revisão das alterações do contexto ANTES da confirmação. Mostra só diferenças
// semânticas em relação à versão em uso (a mesma regra do servidor): quantidade, seções afetadas, campo, antes, agora, tipo,
// origem, autor, data e o efeito nas análises. A confirmação só aparece depois da revisão, com o efeito explicado; o motivo
// é opcional. Sem diferença real não há botão de confirmar (o servidor também recusa versão igual à anterior).
import { useState, type FormEvent } from 'react';
import { CONTEXT_CHANGE_KIND_LABEL, type ContextChangesDto } from '@intelimove/contracts';
import { AsyncView } from '../../components/States';
import { useToast } from '../../components/Toast';
import { contextAdapter } from '../../data/context';
import { useResource } from '../../hooks/useResource';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function ContextChanges({ revision, canEdit, onConfirmed, onClose }: { revision: number; canEdit: boolean; onConfirmed: () => void; onClose: () => void }) {
  const { state, reload } = useResource((s) => contextAdapter.changes(s), [revision]);
  return (
    <section className="card ctx-changes" aria-labelledby="ctx-changes-t" id="alteracoes" style={{ gridColumn: '1 / -1' }}>
      <AsyncView state={state} onRetry={reload} empty={null}>
        {(d) => <ChangesBody d={d} revision={revision} canEdit={canEdit} onConfirmed={onConfirmed} onClose={onClose} />}
      </AsyncView>
    </section>
  );
}

function ChangesBody({ d, revision, canEdit, onConfirmed, onClose }: { d: ContextChangesDto; revision: number; canEdit: boolean; onConfirmed: () => void; onClose: () => void }) {
  const toast = useToast();
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!d.changes.length) {
    return (
      <>
        <h2 id="ctx-changes-t" className="card-title">Nenhuma alteração para confirmar</h2>
        <p className="cell-sub wide">O contexto atual é igual, em conteúdo, à versão v{d.baseVersion} em uso. Mudanças só de ordem, formatação ou dados técnicos não geram nova versão.</p>
        <div className="row-actions"><button className="secondary" onClick={onClose}>Fechar</button></div>
      </>
    );
  }
  const confirm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const reason = String(new FormData(e.currentTarget).get('reason') ?? '');
    setBusy(true); setError(null);
    try {
      const v = await contextAdapter.confirm(reason, revision);
      toast(`Versão v${v.version} confirmada. As próximas análises passam a usar estas alterações.`);
      onConfirmed();
    } catch (err) { setError((err as Error).message); setBusy(false); }
  };
  const bySection = d.sections.map((s) => ({ ...s, items: d.changes.filter((c) => c.section === s.section) }));
  return (
    <>
      <div className="card-head">
        <div>
          <h2 id="ctx-changes-t" className="card-title">{plural(d.changes.length, 'alteração posterior', 'alterações posteriores')} à versão v{d.baseVersion}</h2>
          <p className="card-sub">Seções afetadas: {d.sections.map((s) => `${s.label} (${s.count})`).join(' · ')}</p>
        </div>
        <button className="secondary" onClick={onClose}>Fechar</button>
      </div>
      {bySection.map((s) => (
        <div key={s.section} className="ctx-change-sec">
          <h3 className="mini-title">{s.label}</h3>
          <p className="cell-sub wide">{s.impact}</p>
          <ul className="ctx-change-list">
            {s.items.map((c, i) => (
              <li key={`${c.entityId ?? 'p'}-${c.field ?? c.kind}-${i}`}>
                <div className="ctx-change-head"><b>{c.entityName ?? c.fieldLabel}</b> <span className="tag gray">{CONTEXT_CHANGE_KIND_LABEL[c.kind]}</span></div>
                {c.entityName && c.fieldLabel && <span className="cell-sub">{c.fieldLabel}</span>}
                {(c.before !== null || c.after !== null) && (
                  <dl className="ctx-before-after">
                    {c.before !== null && <><dt>Antes</dt><dd>{c.before}</dd></>}
                    {c.after !== null && <><dt>Agora</dt><dd>{c.after}</dd></>}
                  </dl>
                )}
                <span className="cell-sub">{[c.origin ? `Origem: ${c.origin}` : null, c.author ? `por ${c.author}` : null, when(c.at)].filter(Boolean).join(' · ') || 'Origem não registrada no histórico (alteração anterior ao registro detalhado).'}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {canEdit && (!reviewed ? (
        <div className="row-actions"><button className="cta" onClick={() => setReviewed(true)} disabled={!d.canConfirm} title={d.blockedReason ?? undefined}>Revisei as alterações</button>
          {d.blockedReason && <span className="cell-sub">{d.blockedReason}</span>}</div>
      ) : (
        <form className="ctx-confirm" onSubmit={(e) => void confirm(e)} aria-labelledby="ctx-confirm-t">
          <h3 id="ctx-confirm-t" className="mini-title">Confirmar nova versão</h3>
          <p>Ao confirmar, estas alterações passarão a orientar as próximas análises do InteliMove. A versão v{d.baseVersion} continua guardada no histórico.</p>
          <div className="field"><label htmlFor="ctx-reason">Motivo (opcional)</label><input id="ctx-reason" name="reason" maxLength={500} placeholder="Ex.: ofertas relacionadas aos perfis de cliente" /></div>
          {error && <p role="alert" className="form-error">{error}</p>}
          <div className="row-actions"><button type="button" className="secondary" onClick={() => setReviewed(false)}>Voltar</button><button className="cta" disabled={busy}>{busy ? 'Confirmando…' : `Confirmar versão v${(d.baseVersion ?? 0) + 1}`}</button></div>
        </form>
      ))}
    </>
  );
}
